import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { TicketEditsDto } from 'src/api/contracts';
import ticketEditsApiService from 'src/api/services/ticketEditsApiService';
import { AppState } from 'src/store/store';
import { TicketEdits, TicketEditsPeriod } from './types';

const initialState: TicketEdits = {
    status: 'initial',
    items: [],
    total: 0,
    period: null,
    pagination: {
        pageNumber: 1,
        pageSize: 100
    },
    selected: [],
    requestId: ''
};

export const loadTicketEditsCurrentPage = createAsyncThunk<TicketEditsDto, void, { state: AppState }>(
    'ticket-edits/load-curent-page',
    async (_, { getState }) => {
        const state = getState();
        const period = selectTicketEditsPeriod(state);
        if (!period || !period.dateStart || !period.dateEnd) {
            throw new Error('Не задан период');
        }
        const pagination = selectTicketEditsPagination(state);
        const resp = await ticketEditsApiService.loadTicketEdits({
            filter: {
                issuedOperationDateStart: period.dateStart.format('YYYY-MM-DD'),
                issuedOperationDateEnd: period.dateEnd.format('YYYY-MM-DD')
            },
            pagination: {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize
            }
        });
        return resp;
    }
);

export const loadTicketEditsNextPage = createAsyncThunk<
    TicketEditsDto,
    { pageNumber: number; pageSize?: number },
    { state: AppState }
>('ticket-edits/load-next-page', async (nextPagination: { pageNumber: number; pageSize?: number }, { getState }) => {
    const state = getState();
    const period = selectTicketEditsPeriod(state);
    if (!period || !period.dateStart || !period.dateEnd) {
        throw new Error('Не задан период');
    }
    const pagination = selectTicketEditsPagination(state);
    const resp = await ticketEditsApiService.loadTicketEdits({
        filter: {
            issuedOperationDateStart: period.dateStart.format('YYYY-MM-DD'),
            issuedOperationDateEnd: period.dateEnd.format('YYYY-MM-DD')
        },
        pagination: {
            pageNumber: nextPagination.pageNumber,
            pageSize: nextPagination.pageSize ?? pagination.pageSize
        }
    });
    return resp;
});

export const deleteTicketEdits = createAsyncThunk<void, void, { state: AppState }>(
    'ticket-edits/delete',
    async (_, { getState }) => {
        const selected = selectTicketEditsSelection(getState());
        await ticketEditsApiService.deleteCouponEdits(selected);
    }
);

const slice = createSlice({
    name: 'ticket-edits',
    initialState,
    reducers: {
        periodChanged: (state, action: PayloadAction<TicketEditsPeriod>) => {
            state.period = action.payload;
            state.items = [];
            state.total = 0;
            state.pagination.pageNumber = 1;
        },
        selectionChanged: (state, action: PayloadAction<number[]>) => {
            state.selected = action.payload;
        },
        reset: () => initialState
    },
    extraReducers(builder) {
        builder
            .addCase(loadTicketEditsCurrentPage.pending, (state, action) => {
                state.status = 'loading';
                state.selected = [];
                state.requestId = action.meta.requestId;
            })
            .addCase(loadTicketEditsCurrentPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                }
            })
            .addCase(loadTicketEditsCurrentPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                    state.total = 0;
                }
            })
            .addCase(loadTicketEditsNextPage.pending, (state, action) => {
                state.status = 'loading';
                state.selected = [];
                state.pagination.pageNumber = action.meta.arg.pageNumber;
                if (action.meta.arg.pageSize) {
                    state.pagination.pageSize = action.meta.arg.pageSize;
                }
                state.requestId = action.meta.requestId;
            })
            .addCase(loadTicketEditsNextPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                    state.pagination.pageSize = action.payload.pageSize;
                }
            })
            .addCase(loadTicketEditsNextPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.items = [];
                }
            })
            .addCase(deleteTicketEdits.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(deleteTicketEdits.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                    state.selected = [];
                }
            })
            .addCase(deleteTicketEdits.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'done';
                }
            });
    }
});

const selectTicketEdits = (state: AppState) => state.ticketEdits;

export const selectTicketEditsStatus = (state: AppState) => selectTicketEdits(state).status;

export const selectedTicketEditsIsLoading = (state: AppState) => selectTicketEditsStatus(state) === 'loading';

export const selectTicketEditsItems = (state: AppState) => selectTicketEdits(state).items;

export const selectTicketEditsTotal = (state: AppState) => selectTicketEdits(state).total;

export const selectTicketEditsPeriod = (state: AppState) => selectTicketEdits(state).period;

export const selectTicketEditsCanLoadData = createSelector(
    selectTicketEditsPeriod,
    (period) => period && period.dateStart && period.dateEnd
);

export const selectTicketEditsPagination = (state: AppState) => selectTicketEdits(state).pagination;

export const selectTicketEditsSelection = (state: AppState) => selectTicketEdits(state).selected;

export const { periodChanged, selectionChanged, reset } = slice.actions;

export default slice.reducer;
